import React, { useState } from 'react';
import { useMetronome } from '../contexts/MetronomeContext';
import { useLanguage } from '../contexts/LanguageContext';
import VerticalFader from './VerticalFader';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMusic, FiChevronDown, FiChevronUp } = FiIcons;

const RhythmVolumeControls = () => {
  const { currentRhythmType, rhythmComponentVolumes, setRhythmComponentVolumes } = useMetronome();
  const { t } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);

  // Skip if there's only one subdivision (quarter notes)
  if (!currentRhythmType || currentRhythmType.subdivisions <= 1) {
    return (
      <motion.div 
        className="bg-white dark:bg-gray-900 rounded-lg shadow-md overflow-hidden"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="p-4">
          <h3 className="text-lg font-medium mb-3 text-gray-800 dark:text-gray-200 flex items-center">
            <SafeIcon icon={FiMusic} className="w-5 h-5 text-primary-500 mr-3" />
            {t('rhythmVolumeAdjust')}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 italic">
            {t('noSubdivisions')}
          </p>
        </div>
      </motion.div>
    );
  }

  const handleComponentVolumeChange = (subdivisionIndex, newValue) => {
    // Ensure volume is between 0-100, then convert to 0-1 scale for storage
    const normalizedValue = Math.min(100, Math.max(0, newValue));
    setRhythmComponentVolumes(prev => ({
      ...prev,
      [`${currentRhythmType.id}-${subdivisionIndex}`]: normalizedValue / 100
    }));
  };

  const getComponentVolume = (subdivisionIndex) => {
    const key = `${currentRhythmType.id}-${subdivisionIndex}`;
    // Convert from 0-1 scale to 0-100 scale for the fader
    const volume = rhythmComponentVolumes[key] !== undefined 
      ? rhythmComponentVolumes[key] * 100 
      : 100;
    return Math.min(100, Math.max(0, volume)); // Ensure it's capped at 0-100
  };

  // Create responsive layout based on number of subdivisions
  const getGridCols = () => {
    const count = currentRhythmType.subdivisions;
    if (count <= 2) return 'grid-cols-2';
    if (count <= 3) return 'grid-cols-3';
    if (count <= 4) return 'grid-cols-2 md:grid-cols-4';
    if (count <= 5) return 'grid-cols-3 md:grid-cols-5';
    if (count <= 6) return 'grid-cols-3 md:grid-cols-6';
    if (count <= 7) return 'grid-cols-4 md:grid-cols-7';
    return 'grid-cols-4 md:grid-cols-8';
  };

  // Determine if we need scrollable container for many subdivisions
  const needsScrollContainer = currentRhythmType.subdivisions > 4;

  return (
    <motion.div 
      className="bg-white dark:bg-gray-900 rounded-lg shadow-md overflow-hidden"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Mobile: Collapsible header */}
      <div className="block md:hidden">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full p-4 flex items-center justify-between bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <div className="flex items-center">
            <SafeIcon icon={FiMusic} className="w-5 h-5 text-primary-500 mr-3" />
            <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200">
              {t('rhythmVolumeAdjust')}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600 dark:text-gray-400">
              {currentRhythmType.subdivisions} parts
            </span>
            <SafeIcon 
              icon={isExpanded ? FiChevronUp : FiChevronDown} 
              className="w-5 h-5 text-gray-500" 
            />
          </div>
        </button>
        
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="p-4 space-y-4">
                {/* Mobile: Horizontal sliders for each component */}
                {Array.from({ length: currentRhythmType.subdivisions }, (_, i) => (
                  <motion.div 
                    key={i}
                    className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: i * 0.05 }}
                  >
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-100 dark:bg-primary-900/30 px-2 py-1 rounded">
                          {currentRhythmType.componentLabels?.[i] || (i + 1)}
                        </span>
                      </div>
                      <span className="text-sm bg-gray-100 dark:bg-gray-700 rounded px-2 py-1 font-mono">
                        {Math.round(getComponentVolume(i))}%
                      </span>
                    </div>
                    
                    <input 
                      type="range" 
                      min="0" 
                      max="100" 
                      value={getComponentVolume(i)}
                      onChange={(e) => handleComponentVolumeChange(i, parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
                      style={{
                        background: `linear-gradient(to right, #ec4899 0%, #ec4899 ${getComponentVolume(i)}%, #e5e7eb ${getComponentVolume(i)}%, #e5e7eb 100%)`
                      }}
                    />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Desktop: Original layout */}
      <div className="hidden md:block p-4">
        <h3 className="text-lg font-medium mb-4 text-gray-800 dark:text-gray-200 flex items-center">
          <span className="inline-block w-2 h-6 bg-primary-500 rounded-sm mr-2"></span>
          {t('rhythmVolumeAdjust')}
        </h3>
        
        {needsScrollContainer ? (
          <div className="overflow-x-auto pb-4 -mx-4 px-4">
            <div className={`grid ${getGridCols()} gap-8 min-w-max`}>
              {Array.from({ length: currentRhythmType.subdivisions }, (_, i) => renderFader(i))}
            </div>
          </div>
        ) : (
          <div className={`grid ${getGridCols()} gap-4 sm:gap-6`}>
            {Array.from({ length: currentRhythmType.subdivisions }, (_, i) => renderFader(i))}
          </div>
        )}
        
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-6 text-center italic">
          {t('noteTypes')[currentRhythmType.id]} {t('rhythmVolumeAdjust')}
        </div>
      </div>
    </motion.div>
  );

  // Helper function to render each fader with consistent styling
  function renderFader(i) {
    return (
      <motion.div 
        key={i} 
        className="flex flex-col items-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ 
          duration: 0.4,
          delay: i * 0.07,
          ease: [0.19, 1.0, 0.22, 1.0] // Expo ease out
        }}
      >
        <div className="text-sm text-primary-600 dark:text-primary-400 mb-2 font-medium bg-primary-50 dark:bg-primary-900/30 px-3 py-0.5 rounded-full">
          {currentRhythmType.componentLabels?.[i] || (i + 1)}
        </div>
        
        <div className="bg-white dark:bg-gray-900 p-2 rounded-lg shadow-sm">
          <VerticalFader
            value={getComponentVolume(i)}
            onChange={(value) => handleComponentVolumeChange(i, value)}
            min={0}
            max={100}
            step={1}
          />
        </div>
      </motion.div>
    );
  }
};

export default RhythmVolumeControls;